import { useEffect, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { getMySettings } from '../services/api';
import type { User, UserSettings } from '../types';

const USER_SETTING_EVENT = 'auth:user-setting-updated';
const CHANNEL_NAME = 'auth:user-setting';
// Focus and visibilitychange usually fire together; refetch once per burst.
const REFRESH_MIN_INTERVAL_MS = 5000;

interface UserSettingMessage {
  userId: string;
  detail: UserSettings;
}

const mergeUserSetting = (setUser: Dispatch<SetStateAction<User | null>>, detail: UserSettings) => {
  setUser((prev) => (prev ? { ...prev, setting: { ...prev.setting, ...detail } } : prev));
};

/**
 * Merges `auth:user-setting-updated` events into the user state and relays them
 * to other tabs of the same user, so e.g. a background theme change applies everywhere.
 * Settings are also refetched when a tab regains focus, which covers tabs on other
 * origins or devices that the BroadcastChannel cannot reach.
 */
export function useUserSettingSync(
  userId: string | undefined,
  setUser: Dispatch<SetStateAction<User | null>>
): void {
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  useEffect(() => {
    const channel =
      typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL_NAME);

    const handleLocalUpdate = (event: Event) => {
      const detail = (event as CustomEvent<UserSettings>).detail;
      if (!detail) return;

      mergeUserSetting(setUser, detail);
      if (channel && userIdRef.current) {
        const message: UserSettingMessage = { userId: userIdRef.current, detail };
        channel.postMessage(message);
      }
    };

    const handleRemoteUpdate = (event: MessageEvent<UserSettingMessage>) => {
      const message = event.data;
      if (!message?.detail || message.userId !== userIdRef.current) return;
      mergeUserSetting(setUser, message.detail);
    };

    window.addEventListener(USER_SETTING_EVENT, handleLocalUpdate);
    channel?.addEventListener('message', handleRemoteUpdate);
    return () => {
      window.removeEventListener(USER_SETTING_EVENT, handleLocalUpdate);
      channel?.close();
    };
  }, [setUser]);

  useEffect(() => {
    if (!userId) return;

    let lastRefreshAt = 0;

    const refreshSettings = async () => {
      if (document.visibilityState !== 'visible') return;

      const now = Date.now();
      if (now - lastRefreshAt < REFRESH_MIN_INTERVAL_MS) return;
      lastRefreshAt = now;

      const result = await getMySettings();
      // Ignore responses that arrive after a logout or account switch.
      if (!result.success || !result.data || userIdRef.current !== userId) return;

      mergeUserSetting(setUser, result.data);
    };

    window.addEventListener('focus', refreshSettings);
    document.addEventListener('visibilitychange', refreshSettings);
    return () => {
      window.removeEventListener('focus', refreshSettings);
      document.removeEventListener('visibilitychange', refreshSettings);
    };
  }, [setUser, userId]);
}
